import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container, Paper, Typography, Box, TextField, Button,
  Checkbox, IconButton, Stack, Alert, Snackbar,
  Divider, Grid, Card
} from "@mui/material";
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';

import {
  getShoppingList,
  addCustomShoppingItem,
  toggleShoppingItem,
  deleteShoppingItem,
  clearCompletedShoppingItems,
  clearAllShoppingItems,
  formatShoppingListAsText
} from "../utils/shoppingListService";

const ShoppingList = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [newItemName, setNewItemName] = useState("");
  const [newItemAmount, setNewItemAmount] = useState("");
  const [newItemUnit, setNewItemUnit] = useState("");
  const [snackbarMessage, setSnackbarMessage] = useState(null);

  const loadItems = () => {
    setItems(getShoppingList());
  };

  useEffect(() => {
    loadItems();
    const handleUpdate = () => loadItems();
    window.addEventListener("shopping_list_updated", handleUpdate);
    return () => window.removeEventListener("shopping_list_updated", handleUpdate);
  }, []);

  const handleAddItem = (e) => {
    e.preventDefault();
    if (!newItemName.trim()) return;
    addCustomShoppingItem(newItemName, newItemAmount, newItemUnit);
    setNewItemName("");
    setNewItemAmount("");
    setNewItemUnit("");
    loadItems();
  };

  const handleToggle = (id) => {
    toggleShoppingItem(id);
    loadItems();
  };

  const handleDelete = (id) => {
    deleteShoppingItem(id);
    loadItems();
  };

  const handleClearCompleted = () => {
    clearCompletedShoppingItems();
    loadItems();
  };

  const handleClearAll = () => {
    if (window.confirm("Are you sure you want to clear your entire grocery list?")) {
      clearAllShoppingItems();
      loadItems();
    }
  };

  const handleCopyText = async () => {
    const text = formatShoppingListAsText();
    try {
      await navigator.clipboard.writeText(text);
      setSnackbarMessage("Shopping list copied to clipboard!");
    } catch (e) {
      setSnackbarMessage("Could not copy automatically. You can select and copy manually.");
    }
  };

  const handleShare = async () => {
    const text = formatShoppingListAsText();
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Astrup Family Grocery List",
          text: text
        });
      } catch (e) {
        // Share dismissed
      }
    } else {
      // Fallback: open SMS with text body
      window.open(`sms:?body=${encodeURIComponent(text)}`);
    }
  };

  const pendingItems = items.filter(i => !i.completed);
  const completedItems = items.filter(i => i.completed);

  return (
    <Container maxWidth="md" sx={{ py: 4, mb: 8 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ShoppingCartIcon sx={{ fontSize: 36, color: '#D28415' }} />
          <Typography variant="h4" sx={{ fontFamily: "'Patrick Hand', cursive", color: '#477491' }}>
            Family Grocery List
          </Typography>
        </Box>

        {items.length > 0 && (
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopyText}
              sx={{ borderColor: '#D28415', color: '#D28415' }}
            >
              Copy List
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<ShareIcon />}
              onClick={handleShare}
              sx={{ bgcolor: '#D28415', '&:hover': { bgcolor: '#b36b10' } }}
            >
              Share / SMS
            </Button>
          </Stack>
        )}
      </Box>

      {/* Add Custom Item Form */}
      <Paper elevation={2} sx={{ p: 2.5, mb: 4, borderRadius: 3, bgcolor: '#FAF7F2' }}>
        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, color: '#333' }}>
          Add Item to Grocery List
        </Typography>
        <Box component="form" onSubmit={handleAddItem}>
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={3} sm={2}>
              <TextField
                size="small"
                fullWidth
                placeholder="Qty (2)"
                value={newItemAmount}
                onChange={(e) => setNewItemAmount(e.target.value)}
              />
            </Grid>
            <Grid item xs={3} sm={2}>
              <TextField
                size="small"
                fullWidth
                placeholder="Unit (cups)"
                value={newItemUnit}
                onChange={(e) => setNewItemUnit(e.target.value)}
              />
            </Grid>
            <Grid item xs={6} sm={6}>
              <TextField
                size="small"
                fullWidth
                required
                placeholder="Item name (e.g. Milk, Olive Oil)..."
                value={newItemName}
                onChange={(e) => setNewItemName(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                fullWidth
                type="submit"
                variant="contained"
                startIcon={<AddIcon />}
                disabled={!newItemName.trim()}
                sx={{ bgcolor: '#477491', '&:hover': { bgcolor: '#385c73' }, fontWeight: 'bold' }}
              >
                Add
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {items.length === 0 ? (
        <Card sx={{ p: 4, textAlign: 'center', borderRadius: 3, bgcolor: '#FAF7F2' }}>
          <ShoppingCartIcon sx={{ fontSize: 60, color: '#bbb', mb: 1 }} />
          <Typography variant="h6" color="textSecondary" gutterBottom>
            Your grocery list is empty!
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            You can add ingredients from any recipe by tapping "Add to Grocery List" on the recipe page.
          </Typography>
          <Button
            variant="contained"
            startIcon={<RestaurantMenuIcon />}
            onClick={() => navigate('/recipes')}
            sx={{ bgcolor: '#D28415', '&:hover': { bgcolor: '#b36b10' }, fontWeight: 'bold' }}
          >
            Browse Recipes
          </Button>
        </Card>
      ) : (
        <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
          {/* TO BUY ITEMS */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6" sx={{ fontFamily: "'Patrick Hand', cursive", color: '#D28415' }}>
                To Buy ({pendingItems.length})
              </Typography>
              {pendingItems.length > 0 && (
                <Typography variant="caption" color="textSecondary">
                  Tap to cross off as you shop
                </Typography>
              )}
            </Box>

            {pendingItems.length === 0 ? (
              <Alert severity="success" sx={{ my: 1 }}>
                All items in your list have been picked up! 🎉
              </Alert>
            ) : (
              <Stack spacing={0.5}>
                {pendingItems.map((item) => (
                  <Box
                    key={item.id}
                    onClick={() => handleToggle(item.id)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1,
                      borderRadius: 2,
                      cursor: 'pointer',
                      transition: 'background 0.2s',
                      '&:hover': { bgcolor: '#f7f4ec' }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                      <Checkbox
                        checked={item.completed}
                        onChange={() => handleToggle(item.id)}
                        sx={{ color: '#D28415', '&.Mui-checked': { color: '#2e7d32' } }}
                      />
                      <Box sx={{ minWidth: 0, ml: 0.5 }}>
                        <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '1.05rem' }}>
                          <strong>{item.amount ? `${item.amount} ` : ""}{item.unit ? `${item.unit} ` : ""}</strong>
                          {item.name}
                        </Typography>
                        {item.sources && item.sources.length > 0 && (
                          <Typography variant="caption" color="textSecondary">
                            For: {item.sources.join(", ")}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>

          {/* COMPLETED ITEMS */}
          {completedItems.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ color: '#888', fontWeight: 'bold' }}>
                    Picked Up ({completedItems.length})
                  </Typography>
                  <Button size="small" color="inherit" onClick={handleClearCompleted}>
                    Clear Picked Up
                  </Button>
                </Box>

                <Stack spacing={0.5}>
                  {completedItems.map((item) => (
                    <Box
                      key={item.id}
                      onClick={() => handleToggle(item.id)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 0.8,
                        borderRadius: 2,
                        cursor: 'pointer',
                        opacity: 0.6,
                        '&:hover': { bgcolor: '#f7f4ec' }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                        <Checkbox
                          checked={item.completed}
                          onChange={() => handleToggle(item.id)}
                          sx={{ color: '#2e7d32' }}
                        />
                        <Typography
                          variant="body1"
                          sx={{ textDecoration: 'line-through', color: 'text.secondary' }}
                        >
                          {item.amount ? `${item.amount} ` : ""}{item.unit ? `${item.unit} ` : ""}{item.name}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </>
          )}

          {/* Bottom Clear All Button */}
          <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #eee', textAlign: 'right' }}>
            <Button
              size="small"
              color="error"
              startIcon={<ClearAllIcon />}
              onClick={handleClearAll}
            >
              Clear Entire List
            </Button>
          </Box>
        </Paper>
      )}

      {/* Feedback Snackbar */}
      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={3000}
        onClose={() => setSnackbarMessage(null)}
        message={snackbarMessage}
      />
    </Container>
  );
};

export default ShoppingList;
